import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import dayjs, { Dayjs } from "dayjs";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import StorageCostDetailsListingTable from "../../components/storage-cost/StorageCostDetailsListingTable";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import ConfirmPopup from "../../../../../views/components/common/pop-up/ConfirmPopup";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../../../models/SortType";
import ShipmentsAPI from "../../../api/ShipmentsAPI";
import {
  StorageCostDetailFilters,
  StorageCostDetailParams,
} from "../../../../../models/StorageCost/StrageCostDetailsFilters";
import {
  getModernPageColors,
  modernFilterPanelHeaderSx,
  modernFilterPanelSx,
  modernPageCardSx,
  modernPageShellSx,
} from "../../../../../styles/modernListingPageSx";

const SEARCH_DEBOUNCE_MS = 400;
const MAX_RECALCULATE_DAYS = 30;

const getDefaultCostDateFrom = () =>
  dayjs().subtract(29, "day").format("YYYY-MM-DD");
const getDefaultCostDateTo = () => dayjs().format("YYYY-MM-DD");
const shipmentsAPI = new ShipmentsAPI();

const inclusiveDayCount = (from: string, to: string) =>
  dayjs(to).startOf("day").diff(dayjs(from).startOf("day"), "day") + 1;

const ensureSelectedOptions = (
  options: FilterValue[],
  selectedValues: FilterValue[],
): FilterValue[] => {
  if (!selectedValues.length) return options;
  const merged = [...options];
  selectedValues.forEach((selected) => {
    if (!merged.some((option) => String(option.id) === String(selected.id))) {
      merged.unshift({ id: selected.id, value: selected.value });
    }
  });
  return merged;
};

const parseDateQueryValue = (
  value: string | null,
  fallback: string,
): string => {
  if (!value) return fallback;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : fallback;
};

const parseCsv = (value: string | null): string[] =>
  (value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const StorageCostDetailListing: React.FC = () => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const isDark = theme.palette.mode === "dark";
  const location = useLocation();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { userStore, storageCostStore } = useStores();
  const { id } = useParams<{ id: string }>();
  const searchParams = new URLSearchParams(location.search);
  const defaultCostDateFrom = getDefaultCostDateFrom();
  const defaultCostDateTo = getDefaultCostDateTo();
  const initialClientSku = parseCsv(searchParams.get("client_sku"));
  const initialFilters: StorageCostDetailParams = {
    costDateFrom: parseDateQueryValue(
      searchParams.get("cost_date_from"),
      defaultCostDateFrom,
    ),
    costDateTo: parseDateQueryValue(
      searchParams.get("cost_date_to"),
      defaultCostDateTo,
    ),
    clientSku: initialClientSku,
    selectedPartnerTag: searchParams.get("partner_tag") || null,
    sortBy: searchParams.get("sort_by") || "date",
    sortType:
      searchParams.get("sort_type") === SortType.ASC
        ? SortType.ASC
        : SortType.DESC,
  };
  const [isLoading, setIsLoading] = useState(true);
  const [draftFilters, setDraftFilters] = useState<StorageCostDetailFilters>({
    isFiltersExpanded: true,
    costDateFrom: dayjs(initialFilters.costDateFrom),
    costDateTo: dayjs(initialFilters.costDateTo),
    selectedClientSku: initialFilters.clientSku.map((value) => ({
      id: value,
      value,
    })),
    selectedPartnerTag: initialFilters.selectedPartnerTag,
    partnerTagSearchText: "",
    clientSkuSearchText: "",
  });
  const [appliedFilters, setAppliedFilters] =
    useState<StorageCostDetailParams>(initialFilters);
  const [clientSkuOptions, setClientSkuOptions] = useState<FilterValue[]>([]);
  const [partnerTagOptions, setPartnerTagOptions] = useState<FilterValue[]>([]);
  const debouncedClientSkuSearch = useDebounce(
    draftFilters.clientSkuSearchText.trim(),
    SEARCH_DEBOUNCE_MS,
  );
  const debouncedPartnerTagSearch = useDebounce(
    draftFilters.partnerTagSearchText.trim(),
    SEARCH_DEBOUNCE_MS,
  );
  const [isRecalculateConfirmOpen, setIsRecalculateConfirmOpen] =
    useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [filterError, setFilterError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");
  const clientSkuDropdownOptions = useMemo(
    () =>
      ensureSelectedOptions(clientSkuOptions, draftFilters.selectedClientSku),
    [draftFilters.selectedClientSku, clientSkuOptions],
  );

  const partnerTagDropdownOptions = useMemo(() => {
    if (!draftFilters.selectedPartnerTag) return partnerTagOptions;
    const exists = partnerTagOptions.some(
      (option) => option.value === draftFilters.selectedPartnerTag,
    );
    if (exists) return partnerTagOptions;
    return [
      {
        id: draftFilters.selectedPartnerTag,
        value: draftFilters.selectedPartnerTag,
      },
      ...partnerTagOptions,
    ];
  }, [draftFilters.selectedPartnerTag, partnerTagOptions]);

  const selectedPartnerTagOption: FilterValue[] =
    draftFilters.selectedPartnerTag
      ? [
          {
            id: draftFilters.selectedPartnerTag,
            value: draftFilters.selectedPartnerTag,
          },
        ]
      : [];

  const validateDateRange = (from: Dayjs | null, to: Dayjs | null) => {
    if (!from?.isValid() || !to?.isValid()) {
      return "Select a start and end date.";
    }
    if (to.isBefore(from, "day")) {
      return "End date must be on or after the start date.";
    }
    return "";
  };

  const fetchClientSkuOptions = async (value: string) => {
    try {
      const response = await shipmentsAPI.searchPacks(value.trim());
      const sourceRows = Array.isArray(response?.result) ? response.result : [];
      const optionsMap = new Map<string, FilterValue>();
      sourceRows.forEach((row: any) => {
        const sku = String(
          row?.client_sku || row?.sku || row?.value || "",
        ).trim();
        if (!sku || optionsMap.has(sku)) return;
        optionsMap.set(sku, { id: sku, value: sku });
      });
      setClientSkuOptions(Array.from(optionsMap.values()));
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          "Unable to fetch client SKUs. Please try again.",
      );
    }
  };

  const fetchPartnerTags = async (value: string) => {
    try {
      const response = await storageCostStore.searchTags(value.trim());
      const sourceRows = Array.isArray(response?.result) ? response.result : [];
      const optionsMap = new Map<string, FilterValue>();
      sourceRows.forEach((row: any) => {
        const optionValue = String(
          typeof row === "string" ? row : row?.value || "",
        ).trim();
        const optionId = String(
          typeof row === "string" ? row : row?.id || row?.value || "",
        ).trim();
        if (!optionId || !optionValue || optionsMap.has(optionId)) return;
        optionsMap.set(optionId, { id: optionId, value: optionValue });
      });
      setPartnerTagOptions(Array.from(optionsMap.values()));
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          "Unable to fetch partner tags. Please try again.",
      );
    }
  };

  const handleClientSkuSearch = (value?: string) => {
    const searchValue = value || "";
    setDraftFilters((prev) => ({ ...prev, clientSkuSearchText: searchValue }));
    if (searchValue.trim().length < 3) {
      setClientSkuOptions([]);
    }
  };

  const handlePartnerTagSearch = (value?: string) => {
    const searchValue = value || "";
    setDraftFilters((prev) => ({
      ...prev,
      partnerTagSearchText: searchValue,
    }));
    if (searchValue.trim().length < 3) {
      setPartnerTagOptions([]);
    }
  };

  const handleSortChange = (column: string, sortType: SortType) => {
    setAppliedFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortType,
    }));
  };

  const getStorageCostDetail = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      await storageCostStore.getStorageCostDetail({
        id: Number(id),
        cost_date_from: appliedFilters.costDateFrom,
        cost_date_to: appliedFilters.costDateTo,
        sort_by: appliedFilters.sortBy || "date",
        sort_type: appliedFilters.sortType || SortType.DESC,
        client_sku:
          appliedFilters.clientSku.length > 0
            ? appliedFilters.clientSku.join(",")
            : undefined,
        partner_tag: appliedFilters.selectedPartnerTag || undefined,
      });
    } catch (error: any) {
      setActionError(
        error?.response?.data?.message ||
          "Unable to fetch storage cost details. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmRecalculateCost = async () => {
    if (!id) return;
    const rangeError = validateDateRange(
      dayjs(appliedFilters.costDateFrom),
      dayjs(appliedFilters.costDateTo),
    );
    const dayCount = inclusiveDayCount(
      appliedFilters.costDateFrom,
      appliedFilters.costDateTo,
    );
    if (rangeError || dayCount > MAX_RECALCULATE_DAYS) {
      setActionError(
        rangeError ||
          "Recalculate supports a date range of 30 days or fewer.",
      );
      setIsRecalculateConfirmOpen(false);
      return;
    }
    try {
      setIsRecalculating(true);
      setActionError("");
      setActionSuccess("");
      const response = await storageCostStore.recalculateStorageCost({
        inventory_id: Number(id),
        start_date: appliedFilters.costDateFrom,
        end_date: appliedFilters.costDateTo,
      });
      const result = response?.result;
      const skipped = Number(result?.skipped_dates) || 0;
      const processed = Number(result?.processed_dates) || 0;
      setIsRecalculateConfirmOpen(false);
      setActionSuccess(
        skipped > 0
          ? `${response?.message || "Storage cost recalculated successfully."} ${processed} dates updated, ${skipped} skipped.`
          : response?.message || "Storage cost recalculated successfully.",
      );
      await getStorageCostDetail();
    } catch (error: any) {
      setActionError(
        error?.response?.data?.message ||
          "Unable to recalculate storage cost. Please try again.",
      );
    } finally {
      setIsRecalculating(false);
    }
  };

  const downloadListingData = async () => {
    if (!id) return;
    try {
      setActionError("");
      const response = await storageCostStore.downloadStorageCostDetailReport({
        id: Number(id),
        cost_date_from: appliedFilters.costDateFrom,
        cost_date_to: appliedFilters.costDateTo,
        sort_by: appliedFilters.sortBy || "date",
        sort_type: appliedFilters.sortType || SortType.DESC,
        client_sku:
          appliedFilters.clientSku.length > 0
            ? appliedFilters.clientSku.join(",")
            : undefined,
        partner_tag: appliedFilters.selectedPartnerTag || undefined,
      });

      if (response?.data) {
        const downloadUrl = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = response.filename
          ? response.filename
          : `Storage_Cost_Detail_Listing_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        setActionSuccess("Storage cost detail report downloaded.");
      } else {
        setActionError("No data available to download.");
      }
    } catch (error: any) {
      setActionError(
        error?.message ||
          error?.response?.data?.message ||
          "Unable to download storage cost detail report. Please try again.",
      );
    }
  };

  const applyFilters = () => {
    const rangeError = validateDateRange(
      draftFilters.costDateFrom,
      draftFilters.costDateTo,
    );
    if (rangeError) {
      setFilterError(rangeError);
      return;
    }
    setFilterError("");
    setActionError("");
    setActionSuccess("");
    setAppliedFilters((prev) => ({
      ...prev,
      costDateFrom:
        draftFilters.costDateFrom?.format("YYYY-MM-DD") ||
        getDefaultCostDateFrom(),
      costDateTo:
        draftFilters.costDateTo?.format("YYYY-MM-DD") || getDefaultCostDateTo(),
      clientSku: draftFilters.selectedClientSku.map((item) => String(item.id)),
      selectedPartnerTag: draftFilters.selectedPartnerTag,
    }));
  };

  const resetFilters = () => {
    const nextFrom = getDefaultCostDateFrom();
    const nextTo = getDefaultCostDateTo();
    setFilterError("");
    setActionError("");
    setActionSuccess("");
    setDraftFilters((prev) => ({
      ...prev,
      costDateFrom: dayjs(nextFrom),
      costDateTo: dayjs(nextTo),
      selectedClientSku: [],
      selectedPartnerTag: null,
      partnerTagSearchText: "",
      clientSkuSearchText: "",
    }));
    setAppliedFilters((prev) => ({
      ...prev,
      costDateFrom: nextFrom,
      costDateTo: nextTo,
      clientSku: [],
      selectedPartnerTag: null,
    }));
  };

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set("cost_date_from", appliedFilters.costDateFrom);
    queryParams.set("cost_date_to", appliedFilters.costDateTo);
    queryParams.set("sort_by", appliedFilters.sortBy);
    queryParams.set("sort_type", appliedFilters.sortType);
    if (appliedFilters.clientSku.length > 0) {
      queryParams.set("client_sku", appliedFilters.clientSku.join(","));
    }
    if (appliedFilters.selectedPartnerTag) {
      queryParams.set("partner_tag", appliedFilters.selectedPartnerTag);
    }
    const parentQuery = new URLSearchParams(location.search).get("parent_query");
    if (parentQuery) queryParams.set("parent_query", parentQuery);
    const nextSearch = `?${queryParams.toString()}`;
    if (nextSearch !== location.search) {
      navigate({ search: nextSearch }, { replace: true });
    }
  }, [navigate, location.search, appliedFilters]);

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    storageCostStore.clearSelectedDetail();
  }, [id, storageCostStore]);

  useEffect(() => {
    if (!userStore.hasPermission([EmployeeRoles.VIEW_STORAGE_COST]) || !id) {
      return;
    }
    getStorageCostDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userStore,
    id,
    appliedFilters.costDateFrom,
    appliedFilters.costDateTo,
    appliedFilters.selectedPartnerTag,
    appliedFilters.clientSku,
    appliedFilters.sortBy,
    appliedFilters.sortType,
  ]);

  useEffect(() => {
    if (debouncedClientSkuSearch.length < 3) return;
    fetchClientSkuOptions(debouncedClientSkuSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedClientSkuSearch]);

  useEffect(() => {
    if (debouncedPartnerTagSearch.length < 3) return;
    fetchPartnerTags(debouncedPartnerTagSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPartnerTagSearch]);

  if (!userStore.hasPermission([EmployeeRoles.VIEW_STORAGE_COST])) {
    return <PermissionBlockLabel />;
  }

  const recalculateDayCount = inclusiveDayCount(
    appliedFilters.costDateFrom,
    appliedFilters.costDateTo,
  );

  return (
    <Box sx={modernPageShellSx(theme)}>
      <Paper sx={modernPageCardSx(theme)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "flex-start" },
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <CustomTypography variant="heading3" color={colors.title}>
              Storage Cost Details
            </CustomTypography>
            <CustomTypography variant="body2" color={colors.subtitle}>
              Daily storage cost lines for inventory #{id}.
            </CustomTypography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              width: { xs: "100%", md: "auto" },
            }}
          >
            <AppButton
              variant="transparentPrimary"
              label="Download Data"
              onClick={downloadListingData}
              customStyles={{ width: { xs: "100%", sm: "auto" }, minWidth: 140 }}
            />
            <AppButton
              variant="primary"
              label="Recalculate Cost"
              onClick={() => {
                if (recalculateDayCount > MAX_RECALCULATE_DAYS) {
                  setActionSuccess("");
                  setActionError(
                    "Recalculate supports a date range of 30 days or fewer.",
                  );
                  return;
                }
                setActionError("");
                setIsRecalculateConfirmOpen(true);
              }}
              disabled={isLoading || isRecalculating}
              customStyles={{ width: { xs: "100%", sm: "auto" }, minWidth: 160 }}
            />
          </Box>
        </Box>

        {actionSuccess ? (
          <Box
            sx={{
              mb: 1.5,
              px: 1.25,
              py: 0.75,
              borderRadius: "6px",
              backgroundColor: isDark
                ? "rgba(16,185,129,0.16)"
                : theme.palette.customColors.successGreenLightBackground,
              border: `1px solid ${
                isDark
                  ? "rgba(16,185,129,0.4)"
                  : theme.palette.customColors.successGreenLightBorder
              }`,
            }}
          >
            <CustomTypography
              variant="heading8"
              color={theme.palette.customColors.successGreen}
            >
              {actionSuccess}
            </CustomTypography>
          </Box>
        ) : null}
        <InlineFormError message={actionError} />

        <Paper variant="outlined" sx={modernFilterPanelSx(theme)}>
          <Box
            onClick={() =>
              setDraftFilters((prev) => ({
                ...prev,
                isFiltersExpanded: !prev.isFiltersExpanded,
              }))
            }
            sx={modernFilterPanelHeaderSx(theme, draftFilters.isFiltersExpanded)}
          >
            <CustomTypography variant="heading6" color={colors.title}>
              Filters
            </CustomTypography>
            <IconButton size="small">
              {draftFilters.isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse
            in={draftFilters.isFiltersExpanded}
            timeout="auto"
            unmountOnExit
          >
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                  <DateRangePickerNew
                    placeholder="Date Range"
                    startDate={draftFilters.costDateFrom}
                    endDate={draftFilters.costDateTo}
                    onRangeSelect={(range) => {
                      if (!range.startDate || !range.endDate) return;
                      setFilterError("");
                      setDraftFilters((prev) => ({
                        ...prev,
                        costDateFrom: range.startDate,
                        costDateTo: range.endDate,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                  <AppSearchDropdownMulti
                    minWidth="100%"
                    isMultiSelect
                    title="Client SKU"
                    value={draftFilters.selectedClientSku}
                    list={clientSkuDropdownOptions}
                    local={false}
                    handleSearch={handleClientSkuSearch}
                    handleSelect={(items: FilterValue[]) => {
                      setDraftFilters((prev) => ({
                        ...prev,
                        selectedClientSku: items,
                        clientSkuSearchText: "",
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                  <AppSearchDropdownMulti
                    minWidth="100%"
                    isMultiSelect={false}
                    title="Partner Tag"
                    value={selectedPartnerTagOption}
                    list={partnerTagDropdownOptions}
                    local={false}
                    handleSearch={handlePartnerTagSearch}
                    handleSelect={(items) => {
                      setDraftFilters((prev) => ({
                        ...prev,
                        partnerTagSearchText: "",
                        selectedPartnerTag:
                          items.length > 0 ? String(items[0].value) : null,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <AppButton
                    variant="primary"
                    label="Apply"
                    onClick={applyFilters}
                    customStyles={{ height: 40, width: "100%" }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear All"
                    onClick={resetFilters}
                    customStyles={{ height: 40, width: "100%" }}
                  />
                </Grid2>
              </Grid2>
              <InlineFormError message={filterError} />
            </Box>
          </Collapse>
        </Paper>

        <Box mt={2}>
          <StorageCostDetailsListingTable
            sortBy={appliedFilters.sortBy}
            sortType={appliedFilters.sortType}
            onSortChange={handleSortChange}
            detailRows={storageCostStore.detailRows || []}
            isLoading={isLoading}
          />
        </Box>
        <ConfirmPopup
          open={isRecalculateConfirmOpen}
          title="Recalculate storage cost?"
          message={`This reprices inventory #${id} from ${appliedFilters.costDateFrom} to ${appliedFilters.costDateTo}. Snapshot quantity, pack size, box size, and added today stay unchanged.`}
          confirmText="Recalculate"
          cancelText="Cancel"
          onCancel={() => setIsRecalculateConfirmOpen(false)}
          onConfirm={handleConfirmRecalculateCost}
          loading={isRecalculating}
        />
      </Paper>
    </Box>
  );
};

export default observer(StorageCostDetailListing);
